import { Link } from "react-router-dom";
import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/button";
import { ConnectFooter } from "@/components/connect/ConnectFooter";
import { DialerLanguageToggle } from "@/components/connect/DialerLanguageToggle";
import { SEOHead } from "@/components/seo/SEOHead";
import { getCanonicalUrl, getBreadcrumbSchema } from "@/config/seo";
import { CONNECT_COUNTRIES } from "@/config/connect-countries";
import { COUNTRY_CODE_SLUGS } from "./CountryCodePage";

export default function CountryCodeHub() {
  const entries = COUNTRY_CODE_SLUGS
    .map((slug) => ({ slug, country: CONNECT_COUNTRIES[slug] }))
    .filter((e) => !!e.country);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="International country codes. how to dial any country | elefa Connect"
        description="Country codes and step-by-step dialling guides for the destinations elefa Connect users call most. Current per-minute rates included on every page."
        canonicalUrl={getCanonicalUrl("/country-code")}
        structuredData={getBreadcrumbSchema([
          { name: "Home", url: "https://elefa.tel/" },
          { name: "Country codes", url: "https://elefa.tel/country-code" },
        ])}
      />

      <nav className="container py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Logo variant="header" size="md" asLink={false} withIcon fullName />
        </Link>
        <div className="flex items-center gap-3">
          <DialerLanguageToggle />
          <Link to="/connect-portal/auth">
            <Button variant="outline" size="sm">Get started</Button>
          </Link>
        </div>
      </nav>

      <main className="container max-w-3xl py-8">
        <header className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">International country codes</h1>
          <p className="text-lg text-muted-foreground mt-3">
            The country code is the short number you place in front of a local phone number to call across borders.
            Pick a country below for the code, the dialling format from the US, UK, Europe and Australia,
            and the current per-minute rate on elefa Connect.
          </p>
        </header>

        <ul className="grid sm:grid-cols-2 gap-3">
          {entries.map(({ slug, country }) => (
            <li key={slug}>
              <Link
                to={`/country-code/${slug}`}
                className="block rounded-xl border bg-card p-4 hover:border-primary transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl" aria-hidden>{country.flag}</span>
                  <div>
                    <div className="font-semibold">{country.name}</div>
                    <div className="text-sm text-muted-foreground font-mono">{country.dialCode}</div>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </main>

      <ConnectFooter />
    </div>
  );
}
